/**
 * BooksTab — books this community is discussing, grouped from posts that
 * carry a book attachment (section 12: Books → Communities → Discussions).
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, ChevronDown } from 'lucide-react';
import { fadeUp, stagger } from '@/lib/motion';
import { cx } from '@/components/site/primitives';
import { toast } from '@/components/ui/sonner';
import { bookHref } from '@/lib/bookModel';
import { resolveMockBookId } from '@/components/bookDetails/utils/resolveMockBookId';
import { useCommunities } from '../context/CommunitiesContext';
import PostCard from '../post/PostCard';
import EmptyState from '../EmptyState';
import BookCoverArt from '@/components/books/BookCoverArt';

export default function BooksTab({ community, role }) {
  const { getPosts } = useCommunities();
  const [expanded, setExpanded] = useState(null);
  const navigate = useNavigate();
  const posts = getPosts(community.id);

  const books = useMemo(() => {
    const map = new Map();
    posts.filter(p => p.book).forEach(p => {
      const key = p.book.title;
      if (!map.has(key)) map.set(key, { ...p.book, posts: [] });
      map.get(key).posts.push(p);
    });
    return [...map.values()];
  }, [posts]);

  return (
    <div className="u-container max-w-2xl py-8">
      {community.currentBook && (
        <div className="mb-8 flex items-center gap-4 rounded-2xl border border-line bg-card p-5 shadow-soft">
          <BookCoverArt book={community.currentBook} className="h-20 w-14 shrink-0 shadow-card" />
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-coral">Currently reading</p>
            <p className="mt-1 font-display text-lg font-semibold text-ink">{community.currentBook.title}</p>
            <p className="text-sm text-subtle">{community.currentBook.author}</p>
          </div>
        </div>
      )}

      {books.length === 0 ? (
        <EmptyState type="discussions" />
      ) : (
        <motion.div variants={stagger} initial="hidden" animate="show" className="space-y-3">
          {books.map((book, i) => {
            const isOpen = expanded === book.title;
            return (
              <motion.div key={book.title} variants={fadeUp} custom={i} className="overflow-hidden rounded-2xl border border-line bg-card shadow-soft">
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(bookHref(resolveMockBookId(book)), { state: { hint: book } })}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(bookHref(resolveMockBookId(book)), { state: { hint: book } }); } }}
                  className="flex w-full cursor-pointer items-center gap-4 p-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral"
                >
                  <BookCoverArt book={book} className="h-16 w-11 shrink-0 shadow-card" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-display text-base font-semibold text-ink">{book.title}</p>
                    <p className="truncate text-xs text-subtle">{book.author}</p>
                    <p className="mt-1 inline-flex items-center gap-1.5 text-xs font-medium text-subtle">
                      <MessageCircle className="h-3.5 w-3.5" /> {book.posts.length} discussion{book.posts.length > 1 ? 's' : ''}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toast(`Added “${book.title}” to your TBR`); }}
                    className="shrink-0 rounded-full border border-line px-3 py-1.5 text-xs font-semibold text-ink transition-colors hover:border-coral/40 hover:text-coral"
                  >
                    + Bookshelf
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setExpanded(isOpen ? null : book.title); }}
                    aria-label={isOpen ? 'Hide discussions' : 'Show discussions'}
                    className="shrink-0 rounded-full p-1 text-subtle hover:text-coral"
                  >
                    <ChevronDown className={cx('h-4 w-4 transition-transform', isOpen && 'rotate-180')} />
                  </button>
                </div>
                <AnimatePresence>
                  {isOpen && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                      <div className="space-y-3 border-t border-line p-4">
                        {book.posts.map(post => <PostCard key={post.id} post={post} communityRole={role} />)}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </motion.div>
      )}
    </div>
  );
}
